import * as Sentry from '@sentry/react';
import axios from 'axios';
import { z } from 'zod';

// Constants
const ROUTE = '/authorize'; // POST

// Note: we are parsing ids as `z.guid()` and not `z.uuid()`
// because `z.uuid()` enforces strict RFC 4122 compliance, which
// some of our ids do not conform to. For example, the finley id
// `00000000-0000-0000-0000-66696e6c6579` is not a valid UUID
// according to following regex:
// "/^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000)$/"

// Types
export const UserTypeStruct = z.enum([
  'BORROWER',
  'CAPITAL_PROVIDER',
  'SUPER_USER',
]);
export type UserType = z.infer<typeof UserTypeStruct>;
const AccessTokenStruct = z.object({
  raw: z.string(),
  payload: z.object({
    id: z.guid(),
    email: z.email(),
    userType: UserTypeStruct,
    iat: z.number().int().positive().optional(), // Issued at
    exp: z.number().int().positive().optional(), // Expires at
  }),
});
export type AccessToken = z.infer<typeof AccessTokenStruct>;
const OrganizationStruct = z.object({
  id: z.guid(),
  name: z.string(),
  slug: z.string(),
});
export const UserStruct = z.object({
  id: z.guid(),
  email: z.email(),
  userType: UserTypeStruct,
  isActive: z.boolean(),
  roleType: z.enum(['ADMIN', 'MEMBER', 'CONTRIBUTOR']),
  firstName: z.string(),
  lastName: z.string(),
  organizationId: z.guid(),
  organization: OrganizationStruct,
});
export type User = z.infer<typeof UserStruct>;
const ResponseLoggedInStruct = z.object({
  status: z.literal('logged-in'),
  accessToken: AccessTokenStruct,
  user: UserStruct,
});
export type ResponseLoggedIn = z.infer<typeof ResponseLoggedInStruct>;
const ResponseLoggedOutStruct = z.object({
  status: z.literal('logged-out'),
  accessToken: z.null(),
  user: z.null(),
});
export type ResponseLoggedOut = z.infer<typeof ResponseLoggedOutStruct>;

// Exports
export const authorizeRequest = async () => {
  try {
    const response = await axios.post(ROUTE, undefined, {
      withCredentials: true,
    });

    // Parse logged-in response
    const parsedResponse = ResponseLoggedInStruct.parse(response.data);
    return parsedResponse;

    // Parse logged-out response
  } catch (error) {
    // Expected 401 errors
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        // Log debug info when 401 occurs right after login (cookie might be missing)
        // eslint-disable-next-line no-console
        console.warn('[authorizeRequest] 401 response - debug info:', {
          responseData: error.response.data,
          // Note: httpOnly cookies won't show here, but other cookies will
          visibleCookies: document.cookie || '(none visible)',
          timestamp: new Date().toISOString(),
        });
        return ResponseLoggedOutStruct.parse(error.response.data);
      }
      if (error.code === 'ECONNABORTED') {
        // Request aborted, almost certainly because of redirect. This can occur if a user navigates
        // to the `/auth/logout` route on admin, but isn't logged in.
        // !TODO @eldarshamukhamedov Probably need to get rid of the `/auth/logout` admin route
        Sentry.captureMessage(error.message, Sentry.Severity.Warning);
        throw error;
      }
    }
    // All other errors - log detailed info to help debug
    // eslint-disable-next-line no-console
    console.error('[authorizeRequest] Unexpected error:', {
      error,
      isAxiosError: axios.isAxiosError(error),
      status: axios.isAxiosError(error) ? error.response?.status : undefined,
      data: axios.isAxiosError(error) ? error.response?.data : undefined,
    });
    Sentry.captureException(error);
    throw error;
  }
};
